import { PIN_TYPE } from 'stores/PrefillStore'
import { getStorage } from 'utils/storage'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import { LocalStorage } from 'utils/storage/localStorage'

const PREFILL_STATUS_KEY = 'prefill_status'
const PREFILL_STATUS_EVENT = 'prefill_status_change'
const PREFILL_PIN_MAX_SEND_ATTEMPTS = 4
const PREFILL_PIN_MAX_AUTH_ATTEMPTS = 3
const PREFILL_RESET_PERIOD_MS = 24 * 60 * 60 * 1000 // 24 hours in milliseconds

const getPrefillResetPeriod = () => {
  try {
    const raw = getStorage('localStorage').getItem(LocalStorage.DEV_CONFIG)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed?.isShortPrefillResetPeriodEnabled) {
        return 5 * 60 * 1000 // 5 minutes
      }
    }
  } catch {}
  return PREFILL_RESET_PERIOD_MS
}

interface PrefillStatus {
  traversalId?: string
  countAuthAttempts?: number
  timestamp?: number
  sms?: number
  email?: number
}

export const getPrefillStatus = (): PrefillStatus =>
  JSON.parse(getStorage('localStorage').getItem(PREFILL_STATUS_KEY) || '{}')

export const setPrefillStatus = (prefill: PrefillStatus) => {
  const prefillWithTimestamp = { ...prefill, timestamp: Date.now() }
  getStorage('localStorage').setItem(
    PREFILL_STATUS_KEY,
    JSON.stringify(prefillWithTimestamp)
  )
  try {
    window.dispatchEvent(
      new CustomEvent(PREFILL_STATUS_EVENT, {
        detail: { prefill: prefillWithTimestamp }
      })
    )
  } catch {}
}

export const subscribePrefillStatus = (
  callback: (prefill: PrefillStatus) => void
) => {
  const handler = (e: Event) => {
    if (
      e instanceof CustomEvent &&
      e.detail &&
      typeof e.detail === 'object' &&
      'prefill' in e.detail
    ) {
      callback((e.detail as { prefill: PrefillStatus }).prefill)
    } else {
      callback(getPrefillStatus())
    }
  }
  window.addEventListener(PREFILL_STATUS_EVENT, handler)
  return () => window.removeEventListener(PREFILL_STATUS_EVENT, handler)
}

export const requestPin = (pinType: PIN_TYPE) => {
  const prefillObject = getPrefillStatus()
  prefillObject[pinType] = (prefillObject[pinType] || 0) + 1
  setPrefillStatus(prefillObject)
}

export const getRemainingAttempts = () => {
  const prefillObject = getPrefillStatus()
  const currentTotal =
    (prefillObject['sms'] || 0) + (prefillObject['email'] || 0)
  return PREFILL_PIN_MAX_SEND_ATTEMPTS - currentTotal > 0
    ? PREFILL_PIN_MAX_SEND_ATTEMPTS - currentTotal
    : 0
}

export const getRemainingAuthAttempts = () => {
  const prefillObject = getPrefillStatus()
  const authCount = prefillObject['countAuthAttempts'] || 0
  return PREFILL_PIN_MAX_AUTH_ATTEMPTS - authCount > 0
    ? PREFILL_PIN_MAX_AUTH_ATTEMPTS - authCount
    : 0
}

export const authPin = () => {
  const prefillObject = getPrefillStatus()
  prefillObject['countAuthAttempts'] =
    (prefillObject['countAuthAttempts'] || 0) + 1
  setPrefillStatus(prefillObject)
}

export const isPrefillAuthCountExceeded = () => {
  const prefillObject = getPrefillStatus()

  if (!prefillObject['countAuthAttempts']) {
    return false
  }

  return prefillObject['countAuthAttempts'] >= PREFILL_PIN_MAX_AUTH_ATTEMPTS
}

export const isPinRequestLimitExceeded = () => {
  const prefillObject = getPrefillStatus()
  if (
    (prefillObject['sms'] || 0) + (prefillObject['email'] || 0) >=
    PREFILL_PIN_MAX_SEND_ATTEMPTS
  ) {
    return true
  }
  return false
}

export const isPrefillPossible = (traversalId: string) => {
  let prefillStatus: PrefillStatus = JSON.parse(
    getStorage('localStorage').getItem(PREFILL_STATUS_KEY) || '{}'
  )
  if (!prefillStatus['traversalId']) {
    prefillStatus['traversalId'] = traversalId
    setPrefillStatus(prefillStatus)
    return true
  }
  if (prefillStatus['traversalId'] !== traversalId) {
    prefillStatus = { traversalId }
    setPrefillStatus(prefillStatus)
    return true
  }

  if (isPrefillAuthCountExceeded() || isPinRequestLimitExceeded()) {
    // Check if reset period has passed since timestamp
    if (prefillStatus.timestamp) {
      const currentTime = Date.now()
      const timeDiff = currentTime - prefillStatus.timestamp
      const resetPeriod = getPrefillResetPeriod()

      if (timeDiff >= resetPeriod) {
        prefillStatus = { traversalId }
        setPrefillStatus(prefillStatus)
        return true
      }
    }
    return false
  }
  return true
}

export const trackPinCheck = (status: 'success' | 'failure' | 'escape') => {
  const prefillObject = getPrefillStatus()

  trackInAmplitude(AmplitudeEvent.PrefillPinCheck, {
    status,
    numberOfSmsRequested: prefillObject['sms'],
    numberOfEmailRequested: prefillObject['email'],
    numberOfVerificationAttempts: prefillObject['countAuthAttempts']
  })
}
